import React, { useState } from 'react';
import marksAnalyticsApis from '../../apis/examReports';
import { SnackbarSeverityType } from '../../enums';
import ToastMessage from '../../components/ToastMessage';
import { academicYears } from '../../constants/academicYears';
import DropdownSelect from '../../components/DropdownSelect';
import {
  buildExcelFromGenericRows,
  downloadConsolidatedReport,
  downloadSelectedExamReports,
  fetchReport
} from './utils/utils';
import {
  FiltersRow,
  FiltersRowContainer,
  StyledDivider,
  UnauthorizedMessage,
  UnauthorizedMessageText
} from './styled';
import { useMarksAnalytics } from './utils/useMarksAnalytics';
import LoadingBackdrop from '../MarksAnalytics/components/LoadingBackdrop';
import ActionButtons from './components/ActionButtons';
import getAuthorisedUsersForReport from './utils/getAuthorisedUsersForReport';
import { downloadMultipleExamReports } from './utils/downloadReport';
import { PageWrapper, Title } from '../common/styled';
import SelectAll from '../../components/SelectAllField/SelectAllField';

const MarksAnalytics: React.FC = () => {
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState<SnackbarSeverityType>(
    SnackbarSeverityType.success
  );

  const showToast = (message: string, severity: SnackbarSeverityType) => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  const handleCloseToast = () => {
    setToastOpen(false);
  };

  const {
    academicYear,
    setAcademicYear,
    selectedSchool,
    onSchoolChange,
    grade,
    onGradeChange,
    division,
    onDivisionChange,
    examsOptions,
    selectedExamIds,
    setSelectedExamIds,
    derivedTermIds,
    isLoadingClasses,
    isLoadingExams,
    isGenerating,
    setIsGenerating,
    isDownloading,
    setIsDownloading,
    canGenerateOrDownload,
    gradeOptions,
    divisionOptions,
    user,
    schools
  } = useMarksAnalytics({ showToast });

  const isAuthorised = !!(
    user?.email && getAuthorisedUsersForReport.includes(user.email)
  );
  const isGradeDisabled = !selectedSchool || gradeOptions.length === 0;
  const isDivisionDisabled =
    !selectedSchool || !grade || divisionOptions.length === 0;
  const isExamsDisabled =
    !selectedSchool || !grade || !division || isLoadingExams;

  const handleGenerate = async () => {
    if (!canGenerateOrDownload || !user) return;
    setIsGenerating(true);
    try {
      await marksAnalyticsApis.generateConsolidatedMarksReport(selectedSchool, {
        academicYear,
        grade,
        division,
        userId: user.id,
        termIds: derivedTermIds
      });
      showToast(
        'Please Download the report from this page after 2 minutes to get the latest data',
        SnackbarSeverityType.success
      );
    } catch (e) {
      showToast(
        'Failed to trigger report generation',
        SnackbarSeverityType.error
      );
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = async () => {
    if (!canGenerateOrDownload) return;
    setIsDownloading(true);

    try {
      if (selectedExamIds.length > 0) {
        await downloadSelectedExamReports({
          selectedSchool,
          academicYear,
          grade,
          division,
          examsOptions,
          selectedExamIds,
          downloadMultipleExamReports,
          showToast
        });
      } else {
        await downloadConsolidatedReport({
          selectedSchool,
          academicYear,
          grade,
          division,
          fetchReport,
          buildExcelFromGenericRows,
          showToast
        });
      }
    } catch (e: any) {
      showToast('Failed to download report', SnackbarSeverityType.error);
    } finally {
      setIsDownloading(false);
    }
  };

  const onAcademicYearChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | (Event & { target: { value: unknown; name: string } })
  ) => {
    setAcademicYear(event.target.value as string);
  };

  return (
    <>
      <Title>Marks Analytics</Title>
      {!isAuthorised ? (
        <UnauthorizedMessage>
          <StyledDivider />
          <UnauthorizedMessageText variant='h5'>
            This report is available for limited users only
          </UnauthorizedMessageText>
        </UnauthorizedMessage>
      ) : (
        <PageWrapper>
          <FiltersRowContainer>
            <FiltersRow>
              <DropdownSelect
                name='academicYear'
                value={academicYear}
                onChange={onAcademicYearChange}
                label='Academic Year'
                options={academicYears.map((year) => ({
                  label: year.label,
                  value: year.value
                }))}
              />
              <DropdownSelect
                name='school'
                value={selectedSchool}
                onChange={(e) => onSchoolChange(e.target.value as string)}
                placeholder='Select School'
                label='School'
                options={schools.map((school: any) => ({
                  label: school.shortCode,
                  value: school.shortCode
                }))}
              />
              <DropdownSelect
                name='grade'
                value={grade}
                onChange={(e) => onGradeChange(e.target.value as string)}
                placeholder='Select Grade'
                label='Grade'
                options={gradeOptions.map((grade) => ({
                  label: grade,
                  value: grade
                }))}
                disabled={isGradeDisabled || isLoadingClasses}
                loading={isLoadingClasses}
              />
              <DropdownSelect
                name='division'
                value={division}
                onChange={(e) => onDivisionChange(e.target.value as string)}
                placeholder='Select Division'
                label='Division'
                options={divisionOptions.map((division) => ({
                  label: division,
                  value: division
                }))}
                disabled={isDivisionDisabled}
                loading={isLoadingClasses}
              />
              <SelectAll
                label='Select Exams'
                placeholder='Select Exams'
                options={examsOptions.map((option) => ({
                  value: option.id,
                  label: option.name
                }))}
                selected={selectedExamIds}
                onChange={(selectedIds) =>
                  setSelectedExamIds(selectedIds as string[])
                }
                disabled={isExamsDisabled}
              />
            </FiltersRow>
            <ActionButtons
              canGenerateOrDownload={canGenerateOrDownload}
              isGenerating={isGenerating}
              isDownloading={isDownloading}
              onGenerate={handleGenerate}
              onDownload={handleDownload}
            />
          </FiltersRowContainer>
        </PageWrapper>
      )}

      <LoadingBackdrop
        open={isGenerating || isDownloading}
        message={
          isGenerating
            ? 'Triggering report generation...'
            : 'Preparing download...'
        }
      />
      <ToastMessage
        open={toastOpen}
        message={toastMessage}
        severity={toastSeverity}
        onClose={handleCloseToast}
      />
    </>
  );
};

export default MarksAnalytics;
